import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { ErrorMessage, FormTextField } from "components";
import { Notice } from "components/Notice";
import { UseFormContext } from "lib/hooks";
import { GetFormRules } from "lib/validation/formRules";
import { useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import * as labels from "./constants";
import { CardShell, ReadonlyField } from "./shared";

interface containmentInfoFieldNames {
	containmentName: string;
	description: string;
}

const defaultFieldNames: containmentInfoFieldNames = {
	containmentName: "containmentName",
	description: "description",
};

interface containmentInfoCardProps {
	readonly?: boolean;
	control?: any;
	name?: string;
	description?: string;
	comment?: string;
	mccId?: string;
	disabledFields?: {
		containmentName?: boolean;
		description?: boolean;
	};
	fieldNames?: Partial<containmentInfoFieldNames>;
	variant?: "default" | "nested";
	showComment?: boolean;
}

export function ContainmentInfoCard({
	readonly = false,
	control,
	name,
	description,
	comment,
	mccId,
	disabledFields,
	fieldNames: fieldNameOverrides,
	variant = "default",
	showComment = false,
}: containmentInfoCardProps): React.JSX.Element | null {
	const fields = { ...defaultFieldNames, ...fieldNameOverrides };

	if (readonly && !name && !description && !mccId) return null;

	return (
		<CardShell readonly={readonly} label={labels.ContainmentInformation} toolTip="Enter the containment name and description" variant={variant}>
			{readonly ? (
				<>
					{mccId && <ReadonlyField label={labels.MccId} value={mccId} />}
					{name && <ReadonlyField label={labels.ContainmentName} value={name} />}
					{description && <ReadonlyField label={labels.Description} value={description} />}
					{showComment && (
						<ReadonlyField
							label={labels.Comments}
							value={comment?.trim() ? comment : "—"}
							testId="containment-field-comments"
						/>
					)}
				</>
			) : (
				<>
					<Box data-test-selector="containment-name-field">
						<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
							{labels.ContainmentName}
						</Typography>
						<FormTextField
							control={control!}
							name={fields.containmentName}
							label={labels.ContainmentName}
							disabled={disabledFields?.containmentName}
							rules={disabledFields?.containmentName ? undefined : GetFormRules("containmentName")}
							renderError={(errMsg) => <ErrorMessage msg={errMsg} />}
						/>
						{!disabledFields?.containmentName && (
							<Box sx={{ mt: 1.5 }}>
								<Notice
									message="Please note: Containment Name cannot be modified after containment has been created"
									delay={0}
								/>
							</Box>
						)}
					</Box>
					<Box data-test-selector="description-field">
						<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
							{labels.Description}
						</Typography>
						<FormTextField
							control={control!}
							name={fields.description}
							label={labels.Description}
							disabled={disabledFields?.description}
							multiline
							minRows={3}
							maxRows={5}
							rules={disabledFields?.description ? undefined : GetFormRules("description")}
							renderError={(errMsg) => <ErrorMessage msg={errMsg} />}
						/>
					</Box>
					{showComment && control && (
						<CreateCommentFields control={control} descriptionField={fields.description} />
					)}
				</>
			)}
		</CardShell>
	);
}

export function CreateCommentFields({
	control,
	descriptionField,
}: {
	control: any;
	descriptionField: string;
}): React.JSX.Element {
	const { f } = UseFormContext();
	const setValue = f.setValue;
	const descriptionValue = useWatch({ control, name: descriptionField });
	const holdCommentValue = useWatch({ control, name: "holdComment" });
	const useSameAsDescription = useWatch({ control, name: "useSameAsDescription" });
	const descriptionHasText = String(descriptionValue ?? "").trim().length > 0;
	const commentHasText = String(holdCommentValue ?? "").trim().length > 0;
	const checkboxDisabled =
		!descriptionHasText || (!useSameAsDescription && commentHasText);

	useEffect(() => {
		if (!descriptionHasText && useSameAsDescription) {
			setValue("useSameAsDescription", false, {
				shouldDirty: true,
				shouldValidate: true,
			});
			setValue("holdComment", "", {
				shouldDirty: true,
				shouldValidate: true,
			});
			return;
		}
		if (useSameAsDescription) {
			setValue("holdComment", descriptionValue ?? "", {
				shouldDirty: true,
				shouldValidate: true,
			});
		}
	}, [descriptionHasText, descriptionValue, setValue, useSameAsDescription]);

	return (
		<Box data-test-selector="hold-comment-field">
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1,
					mb: 0.25,
				}}
			>
				<Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
					{labels.Comments}
				</Typography>
				<Box data-test-selector="use-same-as-description">
					<Controller
						name="useSameAsDescription"
						control={control}
						render={({ field }) => (
							<FormControlLabel
								disabled={checkboxDisabled}
								sx={{
									mr: 0,
									my: 0,
									"& .MuiFormControlLabel-label": {
										fontSize: "0.8rem",
									},
								}}
								control={
									<Checkbox
										size="small"
										checked={!!field.value}
										disabled={checkboxDisabled}
										onChange={(_, checked) => {
											field.onChange(checked);
											setValue(
												"holdComment",
												checked ? (descriptionValue ?? "") : "",
												{
													shouldDirty: true,
													shouldValidate: true,
												}
											);
										}}
									/>
								}
								label={labels.UseSameAsDescription}
							/>
						)}
					/>
				</Box>
			</Box>
			<FormTextField
				control={control}
				name="holdComment"
				label={labels.Comments}
				disabled={!!useSameAsDescription}
				multiline
				minRows={3}
				maxRows={5}
				rules={GetFormRules("holdComment")}
				renderError={(errMsg) => <ErrorMessage msg={errMsg} />}
			/>
		</Box>
	);
}
